/**
 * Batch 11B · Progress Concern Evidence Assistant — clinician decision support.
 * Shown ONLY when the patient has a deterministic Progress Concern. AI runs only
 * when the clinician explicitly clicks "Review with Evidence AI". Decision
 * support only — never mutates clinical data, never contacts the patient.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, AlertTriangle, Eye, RefreshCw, FileText } from "lucide-react";

const SECTION_LABELS = {
  why_review: "Why this deserves review",
  what_to_reassess: "What to reassess",
  contributing_factors: "Possible contributing factors",
  rehab_considerations: "Rehabilitation considerations",
  when_to_escalate: "When to escalate / reassess clinically",
  evidence_limitations: "Evidence limitations",
};
const SECTION_ORDER = ["why_review", "what_to_reassess", "contributing_factors",
  "rehab_considerations", "when_to_escalate", "evidence_limitations"];
const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ProgressConcernEvidenceAI({ patientId }) {
  const [concern, setConcern] = useState(null); // null=loading, {concern, detail}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/progress-concern/status`, { params: { patient_id: patientId } })
      .then((r) => alive && setConcern(r.data))
      .catch(() => alive && setConcern({ concern: false }));
    return () => { alive = false; };
  }, [patientId]);

  const run = async () => {
    setBusy(true);
    setOpen(true);
    try {
      const { data } = await api.post(`/progress-concern/evidence-review`, { patient_id: patientId });
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evidence AI is unavailable — please retry.");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const openSource = async (sourceId) => {
    try {
      const r = await api.get(`/evidence/sources/${sourceId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the source file"); }
  };

  if (!concern || !concern.concern) return null; // absent when no Progress Concern

  return (
    <>
      <div className="fl-card fl-card-elevated p-5 border-[#f47a20]/40" data-testid="pc-evidence-ai-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 fl-label text-[#f47a20]">
              <AlertTriangle size={15} /> Progress Concern
            </div>
            <div className="text-sm mt-1 text-zinc-200">{concern.detail || "Recent Check-Ins warrant review."}</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Deterministic flag (Batch 9C). AI runs only when you request it, and never contacts the patient.
            </div>
          </div>
          <Button onClick={run} disabled={busy} data-testid="pc-review-with-ai-btn" className="shrink-0">
            <Sparkles size={15} className="mr-2" /> Review with Evidence AI
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pc-evidence-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-[#f47a20]" /> Evidence AI — decision support</DialogTitle>
          </DialogHeader>

          {busy && <div className="text-sm text-zinc-400 py-8 text-center" data-testid="pc-ai-loading">Retrieving approved evidence and analysing…</div>}

          {!busy && res && (
            <div className="space-y-4" data-testid="pc-ai-result">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                <span>Generated {new Date(res.generated_at).toLocaleString("en-GB")}</span>
                <span>· {res.model}</span>
                <span>· AI calls: {res.ai_calls}</span>
                {res.retrieval_method && <span>· retrieval: {res.retrieval_method}</span>}
                <Button size="sm" variant="outline" className="ml-auto" onClick={run} disabled={busy} data-testid="pc-ai-regenerate">
                  <RefreshCw size={13} className="mr-1.5" /> Regenerate
                </Button>
              </div>

              {res.case_attribution_uncertain && (
                <div className="flex items-start gap-2 text-[12px] rounded-md px-3 py-2"
                  style={{ background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.4)", color: "#fcd34d" }}
                  data-testid="pc-ai-case-uncertain">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  This patient has multiple Active Cases, so Case attribution for this patient-level concern is uncertain. Evidence was retrieved broadly.
                </div>
              )}

              {res.status === "no_relevant_evidence" && (
                <div data-testid="pc-ai-no-evidence" className="rounded-md px-3 py-3 text-[13px]"
                  style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", color: "#d4d4d8" }}>
                  <div className="font-medium mb-1">{SECTION_LABELS.why_review}</div>
                  <div className="mb-3 whitespace-pre-wrap">{res.sections?.why_review}</div>
                  <div className="flex items-start gap-2 text-amber-300">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {res.no_evidence_message}
                  </div>
                </div>
              )}

              {res.status === "ok" && (
                <>
                  {SECTION_ORDER.filter((k) => res.sections?.[k]).map((k) => (
                    <div key={k} data-testid={`pc-ai-section-${k}`}>
                      <div className="fl-label text-xs text-[#f47a20] mb-1">{SECTION_LABELS[k]}</div>
                      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{res.sections[k]}</div>
                    </div>
                  ))}

                  <div data-testid="pc-ai-sources">
                    <div className="fl-label text-xs text-[#f47a20] mb-2">Evidence used</div>
                    <div className="space-y-2">
                      {(res.sources || []).map((s) => {
                        const cited = (res.cited_sources || []).includes(s.ref);
                        return (
                          <div key={s.ref} data-testid={`pc-ai-source-${s.source_id}`} className="rounded-lg p-3"
                            style={{ background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.08)" }}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-zinc-100 truncate flex items-center gap-2">
                                  <FileText size={13} className="text-[#f47a20] shrink-0" />{s.ref} · {s.title}
                                </div>
                                <div className="text-[11px] text-zinc-500">
                                  {(s.authors || []).slice(0, 3).join(", ") || "—"}{s.year ? ` · ${s.year}` : ""}
                                  {s.page != null ? ` · p.${s.page}` : ""}{s.section ? ` · ${s.section}` : ""}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <Badge variant="outline" className="text-[10px]">
                                  {s.source_type === "functional_lab_protocol" ? "Functional LAB protocol" : `Research · ${humanize(s.source_type)}`}
                                </Badge>
                                {cited && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Cited</Badge>}
                              </div>
                            </div>
                            <div className="text-[12px] text-zinc-400 mt-2 line-clamp-3">{s.excerpt}</div>
                            <Button size="sm" variant="outline" className="mt-2" onClick={() => openSource(s.source_id)}
                              data-testid={`pc-ai-open-source-${s.source_id}`}>
                              <Eye size={13} className="mr-1.5" /> Open source
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="text-[10px] text-zinc-600 border-t border-white/10 pt-3">
                Decision support only — suggestions require clinical judgement. Nothing here changes the programme, case or roadmap, and nothing is sent to the patient.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
